import React, { useState } from 'react';
import SplineChartV2, { SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2/SplineChartV2';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useMetricFormatter } from 'src/utils/Hooks';
import { useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import LegendWithMetric from 'src/components/BeaconV2Components/Legend/LegendWithMetric';
import BeaconProChart, {
  OptionProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';
import { ContentSplineChartData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import { TimeSeriesPoint } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/classes/TimeSeriesData';

/**
 * Converts TimeSeriesPoint array [weekId, value][] to SplineChartV2Series format
 */
const convertToSplineV2Series = (data: TimeSeriesPoint[], id: string, name: string): SplineChartV2Series => ({
  id,
  name,
  data: data.map(([weekId, value]) => ({ weekId, value }))
});

interface ContentSplineChartProps {
  title?: string;
  queryFn: () => Promise<any>;
  renderSubtitle: (formattedMetric: string, change: number, averageKeywordCount?: number) => React.ReactNode;
  metricType?: METRICTYPE;
  legendDecimalPlaces?: number;
}

// TODO: If possible, we should create a shared component that can be used on other content pages and simply swap the metric we're displaying
export const ContentSplineChart = ({
  title,
  queryFn,
  renderSubtitle,
  metricType = METRICTYPE.PERCENT,
  legendDecimalPlaces = 2
}: ContentSplineChartProps) => {
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const formatMetric = useMetricFormatter();

  return (
    <BeaconProChart queryFn={queryFn} name={`content-spline-chart-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <BeaconProChart.Header>
        <BeaconProChart.Title>
          <Text variant="h4">{title}</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(data: ContentSplineChartData) => {
          const { primaryLegendMetric, percentChange } = data?.legendData;
          const formattedAverage = formatMetric(primaryLegendMetric, metricType, { decimalPlaces: 2 });

          return renderSubtitle(formattedAverage, percentChange, data?.averageKeywordCount);
        }}
      />

      <BeaconProChart.Legend
        render={(data: ContentSplineChartData) => {
          const { primaryLegendMetric, secondaryLegendMetric, percentChange } = data?.legendData;

          return (
            <Flex gap="lg" sx={{ marginTop: '32px' }}>
              <LegendWithMetric
                color="casper"
                displayName={comparisonTimePeriod.displayName}
                metric={formatMetric(secondaryLegendMetric, metricType, { decimalPlaces: legendDecimalPlaces })}
              />
              <LegendWithMetric
                color="primary"
                displayName={mainTimePeriod.displayName}
                metric={formatMetric(primaryLegendMetric, metricType, { decimalPlaces: legendDecimalPlaces })}
                change={formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 1 })}
                direction={getDirection(percentChange)}
              />
            </Flex>
          );
        }}
      />
      <BeaconProChart.Visualization
        render={(data: ContentSplineChartData) => {
          const { primaryMetrics, secondaryMetrics } = data?.chartData;
          const primarySeries = convertToSplineV2Series(primaryMetrics, 'primary', 'Primary');
          const comparisonSeries = convertToSplineV2Series(secondaryMetrics, 'comparison', 'Comparison');

          return (
            <div style={{ marginTop: '32px' }}>
              <SplineChartV2
                primarySeries={primarySeries}
                comparisonSeries={comparisonSeries}
                metricType={metricType}
              />
            </div>
          );
        }}
      />
    </BeaconProChart>
  );
};

interface ContentDropdownSplineChartProps {
  prefix?: string;
  options: OptionProps[];
  renderSubtitle: (selectedOption: string, formattedMetric: string, change: number) => string | React.ReactNode;
}

export const ContentDropdownSplineChart = (props: ContentDropdownSplineChartProps) => {
  const { prefix, options, renderSubtitle } = props;
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const [selectedOption, setSelectedOption] = useState(options[0].id);
  const formatMetric = useMetricFormatter();

  return (
    <BeaconProChart
      name={`content-dropdown-spline-chart-${selectedOption}`}
      queryFn={options.find((opt) => opt.id === selectedOption)?.queryFn}
    >
      <BeaconProChart.Header>
        <BeaconProChart.Title>
          {prefix && <Text variant="h4">{prefix}</Text>}
          <SlDropdownMenu
            options={options.map((opt) => ({
              id: opt.id,
              label: opt.label
            }))}
            buttonVariant="title"
            defaultLabel=""
            onChange={(option) => setSelectedOption(option.id)}
            selectedId={selectedOption}
          />
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(data) => {
          const { primaryLegendMetric, percentChange } = data?.legendData;

          const formattedAverage = formatMetric(primaryLegendMetric, METRICTYPE.PERCENT, { decimalPlaces: 2 });
          return renderSubtitle(selectedOption, formattedAverage, percentChange);
        }}
      />

      <BeaconProChart.Legend
        render={(data: ContentSplineChartData) => {
          const { primaryLegendMetric, secondaryLegendMetric, percentChange } = data?.legendData;

          return (
            <Flex gap="lg" sx={{ marginTop: '32px' }}>
              <LegendWithMetric
                color="casper"
                displayName={comparisonTimePeriod.displayName}
                metric={formatMetric(secondaryLegendMetric, METRICTYPE.PERCENT, { decimalPlaces: 2 })}
              />
              <LegendWithMetric
                color="primary"
                displayName={mainTimePeriod.displayName}
                metric={formatMetric(primaryLegendMetric, METRICTYPE.PERCENT, { decimalPlaces: 2 })}
                change={formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 1 })}
                direction={getDirection(percentChange)}
              />
            </Flex>
          );
        }}
      />
      <BeaconProChart.Visualization
        render={(data: ContentSplineChartData) => {
          const { primaryMetrics, secondaryMetrics } = data?.chartData;
          const primarySeries = convertToSplineV2Series(primaryMetrics, 'primary', 'Primary');
          const comparisonSeries = convertToSplineV2Series(secondaryMetrics, 'comparison', 'Comparison');

          return (
            <div style={{ marginTop: '32px' }}>
              <SplineChartV2
                primarySeries={primarySeries}
                comparisonSeries={comparisonSeries}
                metricType={METRICTYPE.PERCENT}
              />
            </div>
          );
        }}
      />
    </BeaconProChart>
  );
};
